import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Rss, Plus, Trash2, Power, PowerOff, MapPin, Tag, Building2, KeyRound, Bell,
  RefreshCw, Send, CheckCircle2, XCircle, Clock, Mail, Globe, Webhook,
  Loader2,
} from 'lucide-react';
import ScoringProfileEditor from '../components/tenders/ScoringProfileEditor';
import {
  listSubscriptions, deleteSubscription, toggleSubscription,
  followRegion, followCpv, followEntity, followKeyword,
  listSubscriptionDeliveries, listNotifications, markNotificationsRead,
} from '../lib/api';

const CHANNEL_ICONS = {
  in_app: Bell,
  email: Mail,
  webhook: Webhook,
  websocket: Globe,
};

const CHANNEL_LABELS = {
  in_app: 'In-app',
  email: 'Email',
  webhook: 'Webhook',
  websocket: 'WebSocket',
};

const STATUS_STYLES = {
  sent: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  failed: 'bg-red-500/15 text-red-400 border-red-500/30',
  skipped: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};

function timeAgo(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return 'przed chwilą';
    if (diff < 3600) return Math.floor(diff / 60) + ' min temu';
    if (diff < 86400) return Math.floor(diff / 3600) + ' h temu';
    return Math.floor(diff / 86400) + ' dni';
  } catch { return ts; }
}

function SubscriptionCard({ sub, onToggle, onDelete }) {
  const ChannelIcon = CHANNEL_ICONS[sub.delivery.channel] || Bell;
  const filterLabels = [];
  if (sub.filter_spec.regions?.length) filterLabels.push(`${sub.filter_spec.regions.length} regionów`);
  if (sub.filter_spec.cpv_codes?.length) filterLabels.push(`${sub.filter_spec.cpv_codes.length} CPV`);
  if (sub.filter_spec.entity_nips?.length) filterLabels.push(`${sub.filter_spec.entity_nips.length} entity`);
  if (sub.filter_spec.keywords?.length) filterLabels.push(`${sub.filter_spec.keywords.length} słów`);
  if (sub.filter_spec.tender_ids?.length) filterLabels.push('tender');
  if (sub.filter_spec.event_types?.length) filterLabels.push(`${sub.filter_spec.event_types.length} event types`);

  return (
    <Card className={`transition ${!sub.is_active ? 'opacity-60' : ''}`}>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ChannelIcon className="w-4 h-4 text-primary" />
              <span className="font-medium text-sm truncate">{sub.name}</span>
            </div>
            {sub.description && (
              <div className="text-xs text-muted-foreground mb-2">{sub.description}</div>
            )}
            <div className="flex flex-wrap gap-1.5 text-xs">
              <Badge variant="outline" className="text-xs">
                {CHANNEL_LABELS[sub.delivery.channel]} · {sub.delivery.frequency}
              </Badge>
              {filterLabels.map((l, i) => (
                <Badge key={i} variant="outline" className="text-xs bg-muted">
                  {l}
                </Badge>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> {sub.total_matches} matchów
              </span>
              <span className="flex items-center gap-1 text-emerald-400">
                <Send className="w-3 h-3" /> {sub.total_deliveries_success} wysłanych
              </span>
              {sub.total_deliveries_failed > 0 && (
                <span className="flex items-center gap-1 text-red-400">
                  <XCircle className="w-3 h-3" /> {sub.total_deliveries_failed} failed
                </span>
              )}
              <span className="ml-auto">
                {sub.last_matched_at ? `Ostatnio: ${timeAgo(sub.last_matched_at)}` : 'Nigdy'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Switch
              checked={sub.is_active}
              onCheckedChange={() => onToggle(sub)}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm('Usunąć subskrypcję?')) onDelete(sub);
              }}
              className="text-muted-foreground hover:text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickFollowPanel({ onAdded }) {
  const [region, setRegion] = useState('');
  const [cpv, setCpv] = useState('');
  const [keyword, setKeyword] = useState('');
  const [entityNip, setEntityNip] = useState('');
  const [entityName, setEntityName] = useState('');
  const [loading, setLoading] = useState(null);

  const handle = async (kind, fn) => {
    setLoading(kind);
    try {
      const r = await fn();
      if (r.already_subscribed) {
        toast.info('Już subskrybujesz');
      } else {
        toast.success('Subskrypcja utworzona');
        onAdded?.();
      }
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Szybka subskrypcja
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Region */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Region (województwo)
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="np. mazowieckie"
              value={region}
              onChange={e => setRegion(e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              disabled={!region || loading === 'region'}
              onClick={() => handle('region', () => followRegion(region)).then(() => setRegion(''))}
            >
              {loading === 'region' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dodaj'}
            </Button>
          </div>
        </div>

        {/* CPV */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Kod CPV (lub prefix)
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="np. 45233100 lub 45"
              value={cpv}
              onChange={e => setCpv(e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              disabled={!cpv || loading === 'cpv'}
              onClick={() => handle('cpv', () => followCpv(cpv)).then(() => setCpv(''))}
            >
              {loading === 'cpv' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dodaj'}
            </Button>
          </div>
        </div>

        {/* Keyword */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Słowo kluczowe
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="np. modernizacja, dostawa, wyposażenie"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              disabled={!keyword || loading === 'keyword'}
              onClick={() => handle('keyword', () => followKeyword(keyword)).then(() => setKeyword(''))}
            >
              {loading === 'keyword' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dodaj'}
            </Button>
          </div>
        </div>

        {/* Entity */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Building2 className="w-3 h-3" /> Zamawiający (NIP + nazwa)
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="NIP"
              value={entityNip}
              onChange={e => setEntityNip(e.target.value)}
              className="w-32"
            />
            <Input
              placeholder="Nazwa firmy"
              value={entityName}
              onChange={e => setEntityName(e.target.value)}
              className="flex-1"
            />
            <Button
              size="sm"
              disabled={!entityNip || !entityName || loading === 'entity'}
              onClick={() => handle('entity', () => followEntity(entityNip, entityName))
                .then(() => { setEntityNip(''); setEntityName(''); })}
            >
              {loading === 'entity' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Dodaj'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DeliveriesLog({ items }) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
          <Send className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Brak dostaw w logu</p>
          <p className="text-xs mt-1">Dostawy pojawią się gdy matcher wykryje event pasujący do subskrypcji.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {items.map(it => (
        <Card key={it.id}>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className={STATUS_STYLES[it.status] || ''}>
                {it.status === 'sent' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                {it.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                {it.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                {it.status}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium line-clamp-1">{it.event_title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{it.event_summary}</div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                  <span>{it.event_type}</span>
                  <span>· {it.channel}</span>
                  <span>· attempts {it.attempts}</span>
                  {it.error && <span className="text-red-400">⚠ {it.error.slice(0, 50)}</span>}
                  <span className="ml-auto">{timeAgo(it.created_at)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function NotificationsLog({ items, onMarkRead }) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
          <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>Brak powiadomień</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {items.map(n => (
        <Card
          key={n.job_id || n._id}
          className={n.read ? 'opacity-60' : 'border-l-4 border-l-primary/60'}
        >
          <CardContent className="pt-3 pb-3">
            <div className="flex items-start gap-3">
              <Bell className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{n.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{n.summary}</div>
                <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                  <span>{n.event_type}</span>
                  <span className="ml-auto">{timeAgo(n.created_at)}</span>
                </div>
              </div>
              {!n.read && (
                <Button variant="ghost" size="sm" onClick={() => onMarkRead([n.job_id])}>
                  ✓
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Subscriptions({ lang, tenantId }) {
  const [subs, setSubs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, n] = await Promise.all([
        listSubscriptions(),
        listSubscriptionDeliveries({ limit: 30 }),
        listNotifications({ limit: 30 }),
      ]);
      setSubs(s.items || []);
      setDeliveries(d.items || []);
      setNotifications(n.items || []);
      setUnreadCount(n.unread || 0);
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (sub) => {
    try {
      await toggleSubscription(sub.id);
      await fetchAll();
    } catch (e) { toast.error('Błąd: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleDelete = async (sub) => {
    try {
      await deleteSubscription(sub.id);
      toast.success('Usunięto');
      await fetchAll();
    } catch (e) { toast.error('Błąd: ' + (e.response?.data?.detail || e.message)); }
  };

  const handleMarkRead = async (ids = []) => {
    try {
      await markNotificationsRead(ids, ids.length === 0);
      await fetchAll();
    } catch (e) { toast.error('Błąd: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Rss className="w-6 h-6 text-primary" />
            Subscriptions · Follow anything
          </h1>
          <p className="text-sm text-muted-foreground">
            Śledź regiony, CPV, zamawiających, słowa kluczowe · alerty real-time
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-base py-1 px-3">
            <Rss className="w-4 h-4 mr-1.5" /> {subs.filter(s => s.is_active).length} / {subs.length} aktywne
          </Badge>
          {unreadCount > 0 && (
            <Badge variant="outline" className="text-base py-1 px-3 bg-primary/10 text-primary border-primary/30">
              <Bell className="w-4 h-4 mr-1.5" /> {unreadCount} unread
            </Badge>
          )}
          <Button variant="outline" size="sm" onClick={fetchAll}>
            <RefreshCw className="w-4 h-4 mr-1" /> Odśwież
          </Button>
        </div>
      </div>

      {/* Scoring profile editor — top-level, full width (2026-05-06) */}
      <ScoringProfileEditor />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-1">
          <QuickFollowPanel onAdded={fetchAll} />
        </div>

        <div className="lg:col-span-2">
          <Tabs defaultValue="active">
            <TabsList className="grid grid-cols-3 max-w-md mb-3">
              <TabsTrigger value="active">
                Aktywne {subs.length > 0 && `(${subs.length})`}
              </TabsTrigger>
              <TabsTrigger value="notifications">
                Powiadomienia {unreadCount > 0 && `(${unreadCount})`}
              </TabsTrigger>
              <TabsTrigger value="deliveries">
                Log {deliveries.length > 0 && `(${deliveries.length})`}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active">
              {loading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : subs.length === 0 ? (
                <Card>
                  <CardContent className="pt-10 pb-10 text-center text-muted-foreground">
                    <Rss className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>Brak subskrypcji</p>
                    <p className="text-xs mt-1">Użyj panelu "Szybka subskrypcja" aby dodać pierwszą.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {subs.map(s => (
                    <SubscriptionCard
                      key={s.id} sub={s}
                      onToggle={handleToggle} onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="notifications">
              {unreadCount > 0 && (
                <div className="mb-3">
                  <Button variant="outline" size="sm" onClick={() => handleMarkRead([])}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Oznacz wszystkie przeczytane
                  </Button>
                </div>
              )}
              <NotificationsLog items={notifications} onMarkRead={handleMarkRead} />
            </TabsContent>

            <TabsContent value="deliveries">
              <DeliveriesLog items={deliveries} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
